class ThemeAccordion extends HTMLElement {
  #controller = null;
  #trigger = null;
  #panel = null;
  #reducedMotionQuery = null;
  #prefersReducedMotion = false;
  #isOpen = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#trigger = this.querySelector('[data-accordion-trigger]');
    this.#panel = this.querySelector('[data-accordion-panel]');
    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#prefersReducedMotion = this.#reducedMotionQuery.matches;
    this.#reducedMotionQuery.addEventListener('change', (e) => { this.#prefersReducedMotion = e.matches; }, { signal });

    if (!this.#trigger || !this.#panel) {
      return;
    }

    const shouldOpen = this.hasAttribute('data-open');
    this.#applyOpenState(shouldOpen, { animate: false });

    this.#trigger.addEventListener('click', this.#onTriggerClick.bind(this), { signal });
    this.#trigger.addEventListener('keydown', this.#onTriggerKeydown.bind(this), { signal });
    this.#panel.addEventListener('keydown', this.#onPanelKeydown.bind(this), { signal });
    this.#panel.addEventListener('transitionend', this.#onPanelTransitionEnd.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get isOpen() {
    return this.#isOpen;
  }

  toggle() {
    const group = this.closest('theme-accordion-group');

    if (group?.handleAccordionToggle) {
      group.handleAccordionToggle(this);
      return;
    }

    if (this.#isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  open(options = {}) {
    const { animate = true, fromGroup = false } = options;
    const group = this.closest('theme-accordion-group');

    if (group?.handleAccordionOpen && !fromGroup) {
      group.handleAccordionOpen(this, { animate, scroll: true });
      return;
    }

    this.#applyOpenState(true, { animate });
  }

  close(options = {}) {
    const { animate = true } = options;
    this.#applyOpenState(false, { animate });
  }

  #applyOpenState(isOpen, options = {}) {
    const { animate = true } = options;

    if (!this.#trigger || !this.#panel) {
      return;
    }

    this.#isOpen = isOpen;
    this.#trigger.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      this.setAttribute('data-open', '');
      this.#panel.removeAttribute('aria-hidden');
      this.#panel.removeAttribute('inert');

      if (animate && !this.#prefersReducedMotion) {
        requestAnimationFrame(() => {
          if (this.#isOpen) {
            this.classList.add('is-open');
          }
        });
        return;
      }

      this.classList.add('is-open');
      return;
    }

    this.removeAttribute('data-open');
    this.classList.remove('is-open');

    if (!animate || this.#prefersReducedMotion) {
      this.#setPanelHidden(true);
    }
  }

  #setPanelHidden(hidden) {
    if (!this.#panel) {
      return;
    }

    if (hidden) {
      this.#panel.setAttribute('aria-hidden', 'true');
      this.#panel.setAttribute('inert', '');
      return;
    }

    this.#panel.removeAttribute('aria-hidden');
    this.#panel.removeAttribute('inert');
  }

  #onTriggerClick() {
    this.toggle();
  }

  #onTriggerKeydown(event) {
    if (event.key !== 'Escape' || !this.#isOpen) {
      return;
    }

    event.preventDefault();
    this.close();
    this.#trigger?.focus();
  }

  #onPanelKeydown(event) {
    if (event.key !== 'Escape' || !this.#isOpen) {
      return;
    }

    event.preventDefault();
    this.close();
    this.#trigger?.focus();
  }

  #onPanelTransitionEnd(event) {
    if (event.target !== this.#panel || event.propertyName !== 'grid-template-rows') {
      return;
    }

    if (!this.#isOpen) {
      this.#setPanelHidden(true);
    }
  }
}

if (!customElements.get('theme-accordion')) {
  customElements.define('theme-accordion', ThemeAccordion);
}
